import React, { useState, useEffect, useCallback, useRef } from 'react';

interface CustomRadiusSliderProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  min?: number;
  max?: number;
  step?: number;
}

const CustomRadiusSlider: React.FC<CustomRadiusSliderProps> = ({ 
  value, 
  onChange, 
  className = '',
  min = 5,
  max = 200,
  step = 5
}) => {
  const [localValue, setLocalValue] = useState(value || 50);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  // Update local state when prop changes
  useEffect(() => {
    if (value !== undefined && value !== localValue) {
      setLocalValue(value);
    }
  }, [value]);

  // Calculate percentage from value
  const getPercentage = useCallback((val: number) => {
    return ((val - min) / (max - min)) * 100;
  }, [min, max]);

  // Calculate value from percentage
  const getValueFromPercentage = useCallback((percentage: number) => {
    const rawValue = min + (percentage / 100) * (max - min);
    return Math.round(rawValue / step) * step;
  }, [min, max, step]);

  // Handle mouse/touch events
  const handleStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    
    // Handle the initial click/touch position
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    updateValueFromPosition(clientX);
  }, []);

  const updateValueFromPosition = useCallback((clientX: number) => {
    if (!sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    const newValue = getValueFromPercentage(percentage);
    
    if (newValue !== localValue) {
      setLocalValue(newValue);
      onChange(newValue);
    }
  }, [localValue, onChange, getValueFromPercentage]);

  // Mouse/touch move handlers
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      updateValueFromPosition(clientX);
    };

    const handleEnd = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDragging, updateValueFromPosition]);

  // Handle slider track clicks
  const handleTrackClick = useCallback((e: React.MouseEvent) => {
    if (e.target === thumbRef.current) return;
    
    const clientX = e.clientX;
    updateValueFromPosition(clientX);
  }, [updateValueFromPosition]);

  // Keyboard support
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    let newValue = localValue;
    
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        newValue = Math.max(min, localValue - step);
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        newValue = Math.min(max, localValue + step);
        break;
      case 'Home':
        newValue = min;
        break;
      case 'End':
        newValue = max;
        break;
      default:
        return;
    }
    
    e.preventDefault();
    setLocalValue(newValue);
    onChange(newValue);
  }, [localValue, min, max, step, onChange]);

  const percentage = getPercentage(localValue);

  return (
    <div className={`custom-radius-slider py-2 ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <span className="text-gray-900 font-medium text-sm sm:text-base whitespace-nowrap">
          Radius: {localValue}km
        </span>
        <div className="flex-grow">
          <div 
            ref={sliderRef}
            className="relative h-6 flex items-center cursor-pointer touch-none"
            onClick={handleTrackClick}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={localValue}
            aria-label="Search radius"
            tabIndex={0}
            onKeyDown={handleKeyDown}
          >
            {/* Track */}
            <div className="absolute inset-x-0 top-1/2 transform -translate-y-1/2 h-1 bg-gray-300 rounded-full" />
            
            {/* Active track */}
            <div 
              className="absolute top-1/2 left-0 transform -translate-y-1/2 h-1 bg-blue-600 rounded-full transition-all duration-150 ease-out"
              style={{ width: `${percentage}%` }}
            />
            
            {/* Thumb */}
            <div
              ref={thumbRef}
              className={`absolute top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-5 h-5 bg-white border-2 border-blue-600 rounded-full shadow-md cursor-grab transition-all duration-150 ease-out hover:scale-110 focus:scale-110 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 ${
                isDragging ? 'scale-110 cursor-grabbing' : ''
              }`}
              style={{ 
                left: `${percentage}%`,
                touchAction: 'none'
              }}
              onMouseDown={handleStart}
              onTouchStart={handleStart}
            />
          </div>
          
          {/* Value indicators */}
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{min}km</span>
            <span>{max}km</span>
          </div>
        </div>
      </div>
      
      <style jsx>{`
        .custom-radius-slider {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        
        .custom-radius-slider * {
          box-sizing: border-box;
        }
        
        /* Prevent text selection during drag */
        .custom-radius-slider.dragging {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
        
        /* Enhanced mobile touch targets */
        @media (max-width: 768px) {
          .custom-radius-slider [role="slider"] {
            height: 44px; /* Larger touch target for mobile */
          }
          
          .custom-radius-slider .w-5.h-5 {
            width: 24px;
            height: 24px;
          }
        }
        
        /* Accessibility improvements */
        .custom-radius-slider [role="slider"]:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }
        
        /* Smooth animations */
        .custom-radius-slider .transition-all {
          transition-property: width, transform, box-shadow, background-color;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </div>
  );
};

export default CustomRadiusSlider;